/**
 * Sessao ativa: quem esta a usar o aplicativo e em que familia.
 *
 * Estado puro, de proposito. Autenticacao (codigos de convite, PIN, tokens) vive
 * em `@flos/auth`; aqui so guardamos o resultado. Assim o repositorio e o registro
 * de plugins podem depender da sessao sem arrastar a cadeia de autenticacao para
 * dentro dos testes.
 */
import {
  CoreEvents,
  can as canScope,
  createPermissionSet,
  type PermissionSet,
  type Scope,
  type SessionInfo,
} from '@flos/shared';
import type { EventBus, SessionChangedPayload } from './events';

/** Lancado quando algo tenta ler dados sem sessao. Nunca deve chegar a tela. */
export class NoSessionError extends Error {
  readonly code = 'NO_SESSION';
  constructor() {
    super('Nao ha sessao ativa. Entre com o seu perfil para continuar.');
    this.name = 'NoSessionError';
  }
}

export class SessionService {
  #info: SessionInfo | null = null;
  #permissions: PermissionSet = createPermissionSet('viewer');
  readonly #events: EventBus;

  constructor(events: EventBus) {
    this.#events = events;
  }

  /** Sessao atual ou `null`. Use quando a ausencia e um caso normal. */
  peek(): SessionInfo | null {
    return this.#info;
  }

  /** Sessao atual, obrigatoria. Falha alto — ler dados sem sessao e um bug. */
  current(): SessionInfo {
    if (!this.#info) throw new NoSessionError();
    return this.#info;
  }

  get familyId(): string {
    return this.current().familyId;
  }

  get memberId(): string {
    return this.current().memberId;
  }

  get permissions(): PermissionSet {
    return this.#permissions;
  }

  isActive(): boolean {
    return this.#info !== null;
  }

  set(info: SessionInfo, permissions?: PermissionSet): void {
    const previous = this.#info;
    this.#info = info;
    this.#permissions = permissions ?? createPermissionSet(info.role as PermissionSet['role']);
    this.#announce(previous, info);
  }

  /** Afina permissoes sem trocar de membro (ex. o admin concedeu um escopo). */
  setPermissions(permissions: PermissionSet): void {
    this.#permissions = permissions;
    if (this.#info) this.#announce(this.#info, this.#info);
  }

  clear(): void {
    const previous = this.#info;
    this.#info = null;
    this.#permissions = createPermissionSet('viewer');
    if (previous) this.#announce(previous, null);
  }

  /** Sem sessao nao ha permissao nenhuma — nem sequer leitura. */
  can(scope: Scope): boolean {
    if (!this.#info) return false;
    return canScope(this.#permissions, scope);
  }

  #announce(previous: SessionInfo | null, current: SessionInfo | null): void {
    const payload: SessionChangedPayload = { previous, current };
    this.#events.emit(CoreEvents.SESSION_CHANGED, payload);
  }
}
